import { ArrowRight } from "lucide-react";
import { creditNumber, SENAL, senalPrice } from "../shared/catalog";
import type { Account, StudioConfig } from "../shared/types";
import { bilingual, lang, useLang, useText } from "./i18n";
import { day, euro } from "./Plans";

// Señal, the TikTok course, is recommended beside the credits top-up and below
// a video's download. It is in Castilian, so it shows only in Spanish, and
// never to an account that already has it.
const TEXT = bilingual({
  en: {
    offer: (credits: string) =>
      `The same ${credits} credits, with the TikTok course`,
    until: (price: string, date: string) => `${price} until ${date}`,
    then: (price: string) => `then ${price}`,
    hint: ["Posting it? First see ", "what TikTok really measures", "."],
  },
  es: {
    offer: (credits) =>
      `Los mismos ${credits} créditos, con el curso de TikTok`,
    until: (price, date) => `${price} hasta el ${date}`,
    then: (price) => `después, ${price}`,
    hint: ["¿Vas a publicarlo? Mira antes ", "qué mide TikTok de verdad", "."],
  },
});

export const useSenal = (account: Account, config: StudioConfig) =>
  useLang() === "es" && !!config.senal && !config.preview && !account.senal;

/** Señal's site, tagged with where in the studio the visit came from. */
function href(site: string, campaign: string) {
  const url = new URL(site);
  url.searchParams.set("utm_source", "foco");
  url.searchParams.set("utm_medium", "app");
  url.searchParams.set("utm_campaign", campaign);
  return url.toString();
}

function Logo() {
  return (
    <span className="senal-logo">
      <svg viewBox="0 0 48 48" aria-hidden="true">
        <circle className="r" cx="23.6" cy="24.4" r="19.6" />
        <circle className="r" cx="18.4" cy="29.6" r="12.2" />
        <circle cx="13.2" cy="34.8" r="5.2" />
      </svg>
      <span className="wm">
        se<span className="ny">n</span>al
      </span>
    </span>
  );
}

/** The course as another way to the same credits, under the top-up. */
export function SenalOffer({ site }: { site: string }) {
  const t = useText(TEXT);
  const { price, until } = senalPrice();
  return (
    <a
      className="senal-offer"
      href={href(site, "credits")}
      target="_blank"
      rel="noopener"
      aria-label={`Señal: ${t.offer(creditNumber(SENAL.credits, lang()))}`}
    >
      <Logo />
      <span className="so-text">
        <b>{t.offer(creditNumber(SENAL.credits, lang()))}</b>
        <small>
          {until ? (
            <>
              <span className="so-live">
                {t.until(euro(price), day(`${until}T12:00:00Z`))}
              </span>{" "}
              · {t.then(euro(SENAL.price))}
            </>
          ) : (
            euro(price)
          )}
        </small>
      </span>
      <ArrowRight size={16} />
    </a>
  );
}

/** One sentence under a video's download. */
export function SenalHint({
  site,
  className,
}: {
  site: string;
  className: string;
}) {
  const [before, link, after] = useText(TEXT).hint;
  return (
    <p className={`senal-hint ${className}`}>
      {before}
      <a href={href(site, "download")} target="_blank" rel="noopener">
        {link}
      </a>
      {after}
    </p>
  );
}
